// toast_progress_bar.js

/**
 * ToastProgressBar - A lightweight utility to display a styled progress bar in a toast notification
 *
 * @example
 * import ToastProgressBar from './toast_progress_bar';
 *
 * // Create and show toast
 * const toast = new ToastProgressBar({ message: 'Processing files...' });
 * toast.show();
 *
 * // Update progress
 * toast.updateProgress(50);
 *
 * // Complete and hide
 * toast.complete('Process finished!');
 */
export default class ToastProgressBar {
  /**
   * Create a new toast progress bar
   * @param {Object} options - Configuration options
   * @param {string} [options.message='Loading...'] - Initial message to display
   * @param {string} [options.position='bottom-end'] - Position on screen (bottom-end, bottom-start, top-end, top-start, center)
   * @param {number} [options.autoHideDelay=1000] - Milliseconds to wait before hiding after completion
   * @param {boolean} [options.autoHide=true] - Whether to automatically hide after completion
   * @param {Object} [options.styles={}] - Additional custom styles for the toast
   * @param {string} [options.theme='light'] - Theme (light, dark, auto)
   * @param {string} [options.progressColor='primary'] - Bootstrap color for progress bar (primary, success, danger, warning, info)
   * @param {string} [options.backgroundColor='primary'] - Bootstrap color for toast background (primary, success, danger, warning, info, light, dark, white)
   * @param {number} [options.offset=16] - Offset from screen edge in pixels
   */
  constructor(options = {}) {
    this.options = {
      message: options.message || "Loading...",
      position: options.position || "bottom-end",
      autoHideDelay: options.autoHideDelay || 1000,
      autoHide: options.autoHide !== undefined ? options.autoHide : true,
      styles: options.styles || {},
      theme: options.theme || "light",
      progressColor: options.progressColor || "primary",
      backgroundColor: options.backgroundColor || "white",
      offset: options.offset !== undefined ? options.offset : 16,
    };

    this.progress = 0;
    this.isVisible = false;
    this.isCompleting = false; // Flag to prevent recursive calls

    // Elements references (will be created when needed)
    this.element = null;
    this.messageElement = null;
    this.progressBarElement = null;

    // Ensure styles exist
    this.ensureStylesExist();
  }

  /**
   * Show the toast with the initial message
   * @param {string} [message] - Optional message to override the initial one
   * @returns {ToastProgressBar} this instance for chaining
   */
  show(message = null) {
    // Update message if provided
    if (message) {
      this.options.message = message;
    }

    // Create toast if not exists
    if (!this.element) {
      this.createToast();
    }

    // Set initial message
    this.messageElement.textContent = this.options.message;

    // Reset progress
    this.setProgress(0, false);

    // Show toast
    this.element.classList.add("show");
    this.isVisible = true;

    return this;
  }

  /**
   * Update the progress bar value
   * @param {number} value - Progress value (0-100)
   * @returns {ToastProgressBar} this instance for chaining
   */
  updateProgress(value) {
    // Skip if we're already completing to avoid recursive calls
    if (this.isCompleting) {
      return this;
    }

    return this.setProgress(value, true);
  }

  /**
   * Set progress value - internal method that won't trigger completion
   * @param {number} value - Progress value (0-100)
   * @param {boolean} checkCompletion - Whether to check if progress is 100%
   * @returns {ToastProgressBar} this instance for chaining
   * @private
   */
  setProgress(value, checkCompletion = false) {
    // Make sure toast exists
    if (!this.element) {
      this.createToast();
    }

    // Clamp value between 0-100
    this.progress = Math.min(Math.max(0, value), 100);

    // Update progress bar
    if (this.progressBarElement) {
      this.progressBarElement.style.width = `${this.progress}%`;
      this.progressBarElement.setAttribute("aria-valuenow", this.progress);
    }

    // Auto-complete when 100% if requested
    if (
      checkCompletion &&
      this.progress >= 100 &&
      this.options.autoHide &&
      !this.isCompleting
    ) {
      this.isCompleting = true;

      // Auto hide after delay
      setTimeout(() => {
        this.hide();
        this.isCompleting = false;
      }, this.options.autoHideDelay);
    }

    return this;
  }

  /**
   * Increment the progress bar by a given amount
   * @param {number} [amount=10] - Amount to increment (default: 10)
   * @returns {ToastProgressBar} this instance for chaining
   */
  increment(amount = 10) {
    return this.updateProgress(this.progress + amount);
  }

  /**
   * Update the message text
   * @param {string} message - New message to display
   * @returns {ToastProgressBar} this instance for chaining
   */
  updateMessage(message) {
    // Make sure toast exists
    if (!this.element) {
      this.createToast();
    }

    // Update message
    if (this.messageElement) {
      this.messageElement.textContent = message;
    }

    return this;
  }

  /**
   * Update the appearance of the progress bar
   * @param {string} color - Bootstrap color class (primary, success, danger, warning, info)
   * @returns {ToastProgressBar} this instance for chaining
   */
  setProgressColor(color) {
    // Make sure toast exists
    if (!this.element) {
      this.createToast();
    }

    if (this.progressBarElement) {
      // Remove existing color classes
      [
        "bg-primary",
        "bg-success",
        "bg-danger",
        "bg-warning",
        "bg-info",
      ].forEach((cls) => {
        this.progressBarElement.classList.remove(cls);
      });

      // Add the new color class
      this.progressBarElement.classList.add(`bg-${color}`);
    }

    return this;
  }

  /**
   * Update the background color of the toast
   * @param {string} color - Bootstrap color class (primary, success, danger, warning, info, light, dark, white)
   * @returns {ToastProgressBar} this instance for chaining
   */
  setBackgroundColor(color) {
    // Make sure toast exists
    if (!this.element) {
      this.createToast();
    }

    if (this.element) {
      // Remove existing background color classes
      [
        "bg-primary",
        "bg-success",
        "bg-danger",
        "bg-warning",
        "bg-info",
        "bg-light",
        "bg-dark",
        "bg-white",
      ].forEach((cls) => {
        this.element.classList.remove(cls);
      });

      // Add the new background color class
      this.element.classList.add(`bg-${color}`);

      // Adjust text color for dark backgrounds
      if (["primary", "success", "danger", "dark"].includes(color)) {
        this.element.classList.add("text-white");
        this.element.classList.remove("text-dark");
      } else {
        this.element.classList.add("text-dark");
        this.element.classList.remove("text-white");
      }
    }

    return this;
  }

  /**
   * Set the theme of the toast
   * @param {string} theme - Theme (light, dark, auto)
   * @returns {ToastProgressBar} this instance for chaining
   */
  setTheme(theme) {
    // Make sure toast exists
    if (!this.element) {
      this.createToast();
    }

    if (this.element) {
      // Remove existing theme classes
      this.element.classList.remove("theme-light", "theme-dark", "theme-auto");

      // Add the new theme class
      this.element.classList.add(`theme-${theme}`);

      // If specific light/dark, also apply data attribute for styling
      if (theme === "light" || theme === "dark") {
        this.element.setAttribute("data-bs-theme", theme);
      } else {
        this.element.removeAttribute("data-bs-theme");
      }
    }

    return this;
  }

  /**
   * Mark process as complete and auto-hide if enabled
   * @param {string} [message] - Optional completion message
   * @returns {ToastProgressBar} this instance for chaining
   */
  complete(message = null) {
    // Skip if we're already completing to avoid recursive calls
    if (this.isCompleting) {
      return this;
    }

    this.isCompleting = true;

    // Update message if provided
    if (message) {
      this.updateMessage(message);
    }

    // Set progress to 100% but don't check for completion
    this.setProgress(100, false);

    // Auto hide after delay if enabled
    if (this.options.autoHide) {
      setTimeout(() => {
        this.hide();
        this.isCompleting = false;
      }, this.options.autoHideDelay);
    } else {
      this.isCompleting = false;
    }

    return this;
  }

  /**
   * Show an error state
   * @param {string} [message='An error occurred'] - Error message to display
   * @returns {ToastProgressBar} this instance for chaining
   */
  error(message = "An error occurred") {
    // Skip if we're already completing to avoid recursive calls
    if (this.isCompleting) {
      return this;
    }

    this.isCompleting = true;

    // Update message
    this.updateMessage(message);

    // Change progress bar color to danger
    this.setProgressColor("danger");

    // Auto hide after longer delay if enabled
    if (this.options.autoHide) {
      setTimeout(() => {
        this.hide();
        this.isCompleting = false;
      }, this.options.autoHideDelay * 2); // Give more time to read error
    } else {
      this.isCompleting = false;
    }

    return this;
  }

  /**
   * Hide the toast and remove from DOM after transition
   * @returns {ToastProgressBar} this instance for chaining
   */
  hide() {
    if (this.element) {
      // Start hiding transition
      this.element.classList.remove("show");
      this.isVisible = false;

      // Remove from DOM after transition
      setTimeout(() => {
        this.remove();
      }, 300); // Match transition duration
    }

    return this;
  }

  /**
   * Completely remove the toast from DOM
   * @returns {ToastProgressBar} this instance for chaining
   */
  remove() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
      this.element = null;
      this.messageElement = null;
      this.progressBarElement = null;
    }

    return this;
  }

  /**
   * Check if toast is currently visible
   * @returns {boolean} visibility state
   */
  isShown() {
    return this.isVisible;
  }

  /**
   * Get the current progress value
   * @returns {number} current progress (0-100)
   */
  getProgress() {
    return this.progress;
  }

  // PRIVATE METHODS

  /**
   * Create the toast HTML and add to the document
   * @private
   */
  createToast() {
    // Create container
    const toast = document.createElement("div");
    toast.className = `toast-progress-bar ${this.getPositionClasses()} theme-${
      this.options.theme
    } bg-${this.options.backgroundColor}`;

    // Adjust text color for dark backgrounds
    if (
      ["primary", "success", "danger", "dark"].includes(
        this.options.backgroundColor
      )
    ) {
      toast.classList.add("text-white");
    } else {
      toast.classList.add("text-dark");
    }

    toast.setAttribute("role", "alert");
    toast.setAttribute("aria-live", "assertive");
    toast.setAttribute("aria-atomic", "true");

    // Apply Bootstrap theme if specified
    if (this.options.theme === "light" || this.options.theme === "dark") {
      toast.setAttribute("data-bs-theme", this.options.theme);
    }

    // Create toast content with improved structure
    toast.innerHTML = `
      <div class="toast-body">
        <div class="d-flex align-items-center mb-2">
          <span class="toast-message me-auto">${this.options.message}</span>
        </div>
        <div class="progress">
          <div class="progress-bar bg-${this.options.progressColor}" 
               role="progressbar" 
               style="width: ${this.progress}%" 
               aria-valuenow="${this.progress}" 
               aria-valuemin="0" 
               aria-valuemax="100"></div>
        </div>
      </div>
    `;

    // Apply custom styles if provided
    Object.entries(this.options.styles).forEach(([key, value]) => {
      toast.style[key] = value;
    });

    // Add to document
    document.body.appendChild(toast);

    // Store element references
    this.element = toast;
    this.messageElement = toast.querySelector(".toast-message");
    this.progressBarElement = toast.querySelector(".progress-bar");
  }

  /**
   * Get position classes based on configuration
   * @private
   * @returns {string} CSS classes for positioning
   */
  getPositionClasses() {
    const positionOffset = `${this.options.offset}px`;

    switch (this.options.position) {
      case "bottom-start":
        return `position-fixed bottom-0 start-0 m-${
          this.options.offset > 0 ? "3" : "0"
        }`;
      case "top-end":
        return `position-fixed top-0 end-0 m-${
          this.options.offset > 0 ? "3" : "0"
        }`;
      case "top-start":
        return `position-fixed top-0 start-0 m-${
          this.options.offset > 0 ? "3" : "0"
        }`;
      case "center":
        return "position-fixed top-50 start-50 translate-middle";
      case "bottom-end":
      default:
        return `position-fixed bottom-0 end-0 m-${
          this.options.offset > 0 ? "3" : "0"
        }`;
    }
  }

  /**
   * Ensure required styles exist in the document
   * @private
   */
  ensureStylesExist() {
    const styleId = "toast-progress-bar-styles";

    // Only add styles if they don't already exist
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.textContent = `
        .toast-progress-bar {
          z-index: 1050;
          min-width: 250px;
          max-width: 350px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
          overflow: hidden;
        }
        
        .toast-progress-bar.show {
          opacity: 1;
        }
        
        .toast-progress-bar .toast-body {
          padding: 0.75rem 1rem;
          font-family: Outfit;
        }
        
        .toast-progress-bar .toast-message {
          font-weight: 500;
        }
        
        .toast-progress-bar .progress {
          background-color: rgba(0, 0, 0, 0.1);
          height: 6px;
          margin-bottom: 2px;
          border-radius: 3px;
          overflow: hidden;
        }
        
        .toast-progress-bar .progress-bar {
          transition: width 0.3s ease-in-out;
          height: 100%;
        }
        
        .toast-progress-bar.bg-white {
          background-color: #fff;
        }
        
        /* Dark mode support for auto theme */
        @media (prefers-color-scheme: dark) {
          .toast-progress-bar.theme-auto {
            background-color: #343a40;
            color: #e9ecef;
          }
          
          .toast-progress-bar.theme-auto .progress {
            background-color: rgba(255, 255, 255, 0.1);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }
}
